import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button, Checkbox, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { TemplateSection } from '../template-shell';
import { VnnicCustomerFields } from './vnnic-customer-fields';
import { VnnicDeclaresFields } from './vnnic-declares-fields';
import { VnnicAdminFields } from './vnnic-admin-fields';
import type { ContractFormValues } from '../../contract-form';

const STEPS = [
  { label: 'Thông tin chủ thể' },
  { label: 'Thông tin người khai báo' },
  { label: 'Thông tin quản trị' },
  { label: 'Cài đặt thêm' },
] as const;

interface VnnicStepsFormProps {
  /** Absolute path prefix: `products.{i}.service_domain_metadata`. */
  metaPath: string;
}

/**
 * Stepped contact-info form for VNNIC domain registrations. Four steps matching
 * legacy DomainStepsForm: owner (Step 1), declares/UI (Step 2), admin (Step 3),
 * additional settings — sendmail checkbox (Step 4). Navigation is manual; no
 * submit button (submission is handled by the parent contract form).
 */
export function VnnicStepsForm({ metaPath }: VnnicStepsFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const { control, setValue } = useFormContext<ContractFormValues>();

  const sendmail = (useWatch({ control, name: `${metaPath}.sendmail` as never }) as unknown as boolean) ?? true;

  const stepContent: Record<number, React.ReactNode> = {
    1: <VnnicCustomerFields metaPath={metaPath} />,
    2: <VnnicDeclaresFields metaPath={metaPath} />,
    3: <VnnicAdminFields metaPath={metaPath} />,
    4: (
      <Field label="Thông báo email">
        {() => (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox
              checked={sendmail}
              onCheckedChange={(v) =>
                setValue(`${metaPath}.sendmail` as never, (v === true) as never, { shouldDirty: true })
              }
            />
            Gửi email thông báo cho khách hàng
          </label>
        )}
      </Field>
    ),
  };

  return (
    <TemplateSection title="Thông tin đăng ký VNNIC">
      <Tabs value={String(currentStep)} onValueChange={(v) => setCurrentStep(Number(v))}>
        {/* Step tab bar */}
        <TabsList>
          {STEPS.map((step, idx) => {
            const num = idx + 1;
            return (
              <TabsTrigger key={num} value={String(num)}>
                {num}. {step.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Active step content */}
        {STEPS.map((_, idx) => {
          const num = idx + 1;
          return (
            <TabsContent key={num} value={String(num)} style={{ paddingTop: 'var(--vn-space-2)' }}>
              {stepContent[num]}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Prev / Next navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--vn-space-2)' }}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={currentStep === 1}
          onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
        >
          Quay lại
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={currentStep === STEPS.length}
          onClick={() => setCurrentStep((s) => Math.min(STEPS.length, s + 1))}
        >
          Tiếp theo
        </Button>
      </div>
    </TemplateSection>
  );
}
